import * as Sentry from "@sentry/react";
import React, { useCallback, useEffect, useRef } from "react";

import {
	FormDatePicker,
	FormField,
	FormInputText,
	FormSubscribe,
	SingleSelectInput,
	toast,
	usePyForm,
} from "@performyard/py-components";
import type { Option } from "@performyard/py-components/src/types/Option";

import { searchWorkflowTemplates } from "../../../../api/services/defs/workflow-templates";

import { GeneralInfoSchema, NAME_MAX_LENGTH } from "../launch-form";

type TemplateRef = {
	id: string;
	name: string;
};

const TemplateField = () => {
	const form = usePyForm<typeof GeneralInfoSchema>();

	// `SingleSelectInput` applies whatever its `fetchOptions` promise resolves to and,
	// because the search is server-side, does no client-side filtering on top. A slower
	// request for an earlier term would therefore overwrite the newer results and let the
	// user pick from a list that no longer matches what they typed. Stamping each request
	// and re-serving the last applied options for anything but the newest one keeps the
	// dropdown in step with the input regardless of the order responses land in.
	const requestIdRef = useRef(0);
	const appliedOptionsRef = useRef<Option<TemplateRef>[]>([]);

	const fetchOptions = useCallback(async (searchTerm: string): Promise<Option<TemplateRef>[]> => {
		const requestId = ++requestIdRef.current;
		const isStale = () => requestId !== requestIdRef.current;

		try {
			const templates = await searchWorkflowTemplates(searchTerm.trim());
			if (isStale()) {
				return appliedOptionsRef.current;
			}
			appliedOptionsRef.current = templates.map((template) => ({
				label: template.name,
				value: { id: template.id, name: template.name },
			}));
			return appliedOptionsRef.current;
		} catch (error) {
			Sentry.captureException(error);
			console.error("Error searching workflow templates:", error);
			if (isStale()) {
				return appliedOptionsRef.current;
			}
			toast.error("There was an error searching workflow templates.");
			appliedOptionsRef.current = [];
			return appliedOptionsRef.current;
		}
	}, []);

	return (
		<FormField<TemplateRef | null>
			id="template"
			label="Workflow Template"
			input={(field) => (
				<SingleSelectInput<TemplateRef>
					fetchOptions={fetchOptions}
					value={field.value ?? undefined}
					onChange={(value) => {
						field.onChange(value);
						if (value) {
							form.setFieldValue("workflowName", value.name);
						}
					}}
					selectionVariant="combobox"
					placeholder="Make a selection"
					errored={field.errors.length > 0}
					isClearable
					id="template"
					data-testid="launch-workflow-template"
					ariaLabel="Workflow Template"
				/>
			)}
		/>
	);
};

const DirtyWatcher = ({ isDirty, onDirty }: { isDirty: boolean; onDirty: () => void }) => {
	useEffect(() => {
		if (isDirty) onDirty();
	}, [isDirty, onDirty]);
	return null;
};

/**
 * Step 1 — the workflow's basic setup. Field-level validation lives in
 * `GeneralInfoSchema` on the wizard step; this component only renders the form.
 * `onDirty` fires as soon as any field holds a value so the unsaved-changes guard
 * arms before the user ever advances a step (parity with the legacy page).
 */
export const GeneralInfoStep = ({ onDirty }: { onDirty: () => void }) => (
	<div className="flex flex-col gap-4">
		<TemplateField />

		<FormInputText
			id="workflowName"
			label="Workflow Name"
			placeholder="Workflow name"
			maxLength={NAME_MAX_LENGTH}
		/>

		<FormDatePicker
			id="startDate"
			label="Workflow Start Date"
			placeholder="MM/DD/YYYY"
			dateFormat="numeric"
			showMonthAndYearDropdowns
		/>

		<FormSubscribe
			id="general-info-dirty-watcher"
			selector={(state) =>
				Boolean(state.values?.template || state.values?.workflowName || state.values?.startDate)
			}
		>
			{(hasValues) => <DirtyWatcher isDirty={hasValues} onDirty={onDirty} />}
		</FormSubscribe>
	</div>
);
